import React, { Component, useEffect, useState, useRef, useCallback } from 'react';
import { size, get, set, first, last } from 'global/lib/lodash';
import Modal from 'global/components/modal';
import { Typography, Chip } from '@mui/material';
import { useStyles } from './styles';
import Form from 'global/components/form';
import useRetrieveData from 'global/hooks/useRetrieveData';
import ScoreIcon from 'global/components/ScoreIcon';
import { useUser } from 'global/hooks/useUser';
import SnackAlerts from 'global/components/snack-alerts';
import sendData from 'redux/actions/sendData';
import { useDispatch } from 'react-redux';
import { addNewTask } from 'redux/actions/view';
import { fixWeirdDateIssue } from '../../../global/components/form';
import { taskOptions, SETTINGS } from '../../admin/account-settings';
import { getAccountSetting } from 'redux/actions/userLogin';
import { Text } from '../../../global/components/text';
import { useTaskTypes } from '../task-type-utils/useTaskTypes';
import { getAvailableTaskTypes, getAvailableTaskTypesForTier, getCustomFieldInputs, getCloseoutFromTaskType, getCloseoutDefaults, getStandardFieldRequirements, UploadCustomFieldMedia } from '../task-type-utils';
import { splitMediaByType } from 'global/utils/mediaUtils';
import { getDisplayValue } from 'global/utils/getDisplayScore';
import { useAccessEdition } from 'global/utils/edition';
import LibraryIcon from '@mui/icons-material/Book';
import { Link } from 'react-router-dom';
import { UploadFiles } from 'redux/actions/imageUploader';

export const TaskScoreRef = props => {
  const { checkpoint } = props;
  const classes = useStyles();
  const showLink = get(props, 'showLink', false);

  const checkpointId = get(props, 'checkpoint.checkpointId');
  const checklistId = get(props, 'checkpoint.checklistId');
  
  const ed = useRetrieveData(
    { url: `/edition-documents` },
    { 
      waitFor: !!checklistId && showLink ? undefined : null,
      build: ['editionDocuments'], 
      params: { filter: { documentable_type: 'checklist', documentable_id: checklistId } }, 
      showAlert: false, 
      transformData: (d) => last(d) 
    });

  const editionId = get(ed, 'data.editionId');

  const bool = useAccessEdition(editionId);

  const scscore = useRetrieveData(
    { url: `/scorecard-scores/${get(props, 'checkpoint.scorecardScoreId')}` },
    {
      build: ['scorecardScores'],
      waitFor: !!get(props, 'checkpoint.scorecardScoreId') ? undefined : null,
      transformData: d => {
        return first(d);
      }
    }
  );

  const getLink = (() => {
    if (bool) {
      return `/library/${editionId}/document/${get(ed, 'data.classificationId')}${checkpointId ? `?hl=${checkpointId}` : ''}`;
    }
    return '';
  })();

  if (checkpoint) {
    const cpscore = checkpoint.score != undefined ? checkpoint.score : checkpoint.value;
    const sibscore = get(scscore, 'data.statusSiblings', []).find(s => s.status == 'goal' && s.id != checkpoint.id);
    const displayValue = getDisplayValue({ value: cpscore }, get(scscore, 'data.scorecard.config'));
    const sibValue = !!sibscore ? getDisplayValue({ value: sibscore.score }, get(scscore, 'data.scorecard.config')) : null;

    return (
      <div style={{ marginLeft: 8 }}>
        {!!sibscore && <Text className={classes.refLabel}>Your Score</Text>}
        <div className={classes.rowTop}>
          <div className={classes.row}>
            <Typography variant="body2" className={classes.cpScoreHeaderChild}>
              {displayValue}
            </Typography>
            <ScoreIcon center score={cpscore} />
          </div>
          <Typography variant="body2">
            <strong>{checkpoint.name}</strong>
            &nbsp;
            {!!checkpoint.criteria && checkpoint.criteria}
          </Typography>
        </div>

        {!!sibscore && <div style={{ marginTop: 8, marginBottom: 8 }}>
          <div>
            <Text className={classes.refLabel}>Target Score</Text>
          </div>
          <div className={classes.rowTop}>
            <div className={classes.row}>
              <Typography variant="body2" className={classes.cpScoreHeaderChild}>
                {sibValue}
              </Typography>
              <ScoreIcon center score={sibscore.score} />
            </div>
            <Typography variant="body2">
              <strong>{checkpoint.name}</strong>
              &nbsp;
              {!!sibscore.criteria && sibscore.criteria}
            </Typography>
          </div>
        </div>}

        {showLink && getLink && (
          <div className={classes.tagLink}>
            <Text className={classes.tagReference} component={Link} to={getLink}>
              <LibraryIcon />
              Show Reference Document
            </Text>
          </div>
        )}
      </div>
    );
  }

  return null;
}

const CreateTaskWindow = (props) => {
  const { loading, formState, onFormUpdate, checkpoint, topContent, formRef } = props;
  const { me } = useUser();
  const classes = useStyles();
  const { data: taskTypesData } = useTaskTypes();
  const availableTypes = getAvailableTaskTypesForTier(getAvailableTaskTypes(taskTypesData), props.realmTier);
  const selectedTaskType = availableTypes.find((tt) => tt.id === get(formState, 'taskTypeId'));

  useEffect(() => {
    if (size(availableTypes) === 1 && !get(formState, 'taskTypeId')) {
      onFormUpdate({ ...formState, taskTypeId: get(availableTypes, '0.id') });
    }
  }, [availableTypes]);

  // Update closeout defaults when task type changes
  useEffect(() => {
    if (selectedTaskType && formRef && formRef.current) {
      formRef.current.updateState('meta', getCloseoutDefaults(selectedTaskType));
    }
  }, [get(selectedTaskType, 'id')]);

  const usersList = useRetrieveData(
    { url: '/users' },
    {
      build: ['users'],
      params: {
        filter: { content_realm_id: get(props, 'realmId') },
        include: 'realm',
      },
      useCache: { reload: 1 },
    }
  );

  useEffect(() => {
    if (!usersList.loading && props.assignedTo && size(props.assignedTo) > 0) {
      const atolist = props.assignedTo.map((a) => a.id);
      const users = usersList.data.filter((a) => atolist.indexOf(a.id) > -1);
      onFormUpdate({
        ...formState,
        clientId: get(users, '0.realm.id'),
      });
    }
  }, [usersList.data, usersList.loading]);

  const renderContent = () => {
    if (topContent) {
      return topContent();
    }

    return null;
  };

  const stdReqs = selectedTaskType ? getStandardFieldRequirements(selectedTaskType) : [];
  const isFieldRequired = (fid) => stdReqs.find((f) => f.id === fid)?.is_required;
  const isFieldEnabled = (fid) => { const f = stdReqs.find((s) => s.id === fid); return f ? f.is_enabled !== false : true; };

  const defaultFieldMap = {
    f_description: {
      id: 'description',
      type: 'TextField',
      required: isFieldRequired('f_description'),
      defaultValue: () => get(checkpoint, 'notes'),
      props: { placeholder: selectedTaskType ? `Describe your ${selectedTaskType.name}` : 'Describe your task', maxRows: 5, multiline: true },
    },
    f_assignee: {
      id: 'assignedTo',
      type: 'UserSelect',
      required: isFieldRequired('f_assignee'),
      defaultValue: () => !!size(props.assignedTo) ? props.assignedTo : [me],
      data: () => usersList.data,
      props: { label: 'Assigned To' },
    },
    f_due_date: {
      id: 'dueDate',
      type: 'KeyboardDatePicker',
      required: isFieldRequired('f_due_date'),
      props: {
        label: 'Due Date',
        convert: d => fixWeirdDateIssue(d, !props.dueDate),
      },
    },
    f_trade: {
      id: 'tradeTypes',
      type: 'TradeSelect',
      required: isFieldRequired('f_trade'),
      defaultValue: () => get(props, 'checkpoint.tradeTypes', []),
      props: { label: 'Trade Types', checklist: props.checklist },
    },
    f_media: {
      id: 'media',
      type: 'DropArea',
      required: isFieldRequired('f_media'),
      defaultValue: () => get(formState, 'media', []),
      props: {
        label: 'Media', collection: 'task-images', uploadAllNew: true,
        bothMode: true, accept: null,
      },
    },
  };

  const referenceInput = {
    id: 'reference',
    props: { label: 'Reference' },
    component: () => <TaskScoreRef {...props} />,
  };

  const metaInput = {
    id: 'meta',
    type: 'Checkbox',
    options: selectedTaskType ? getCloseoutFromTaskType(selectedTaskType) : taskOptions(),
    defaultValue: () => selectedTaskType ? getCloseoutDefaults(selectedTaskType) : getAccountSetting(SETTINGS.taskDefault),
    props: { label: 'Requirements' },
  };

  const typeSelector = size(availableTypes) > 1 ? {
    id: 'taskTypeId',
    type: 'Select',
    data: availableTypes.map((tt) => ({ title: tt.name, value: tt.id })),
    defaultValue: () => get(formState, 'taskTypeId') || props.taskTypeId || '',
    props: { label: 'Task Type' },
  } : null;

  const buildInputs = () => {
    const fieldOrder = selectedTaskType && get(selectedTaskType, 'field_order');

    if (fieldOrder && Array.isArray(fieldOrder)) {
      const customInputMap = {};
      getCustomFieldInputs(selectedTaskType, formState).forEach((input) => {
        const cfId = input.id.replace('custom_field_values.', '');
        customInputMap[cfId] = input;
      });

      const ordered = [typeSelector];
      fieldOrder.forEach((fid) => {
        if (fid === 'f_title') return; // title auto-generated from checkpoint
        if (fid === 'f_documents') return; // merged into f_media
        if (fid === 'f_reference') {
          ordered.push(referenceInput);
          return;
        }
        if (defaultFieldMap[fid] && isFieldEnabled(fid)) {
          ordered.push(defaultFieldMap[fid]);
        } else if (customInputMap[fid]) {
          ordered.push(customInputMap[fid]);
        }
      });

      // Append custom fields not in field_order
      Object.keys(customInputMap).forEach((cfId) => {
        if (!fieldOrder.includes(cfId)) {
          ordered.push(customInputMap[cfId]);
        }
      });

      ordered.push(metaInput);
      return ordered.filter(Boolean);
    }

    // Fallback: no field_order
    return [
      typeSelector,
      defaultFieldMap.f_description,
      defaultFieldMap.f_assignee,
      defaultFieldMap.f_due_date,
      ...(isFieldEnabled('f_trade') ? [defaultFieldMap.f_trade] : []),
      defaultFieldMap.f_media,
      referenceInput,
      ...(selectedTaskType ? getCustomFieldInputs(selectedTaskType, formState) : []),
      metaInput,
    ].filter(Boolean);
  };

  return (
    <>
      {renderContent()}
      <Form
        ref={formRef}
        loading={loading}
        showAllScreens={true}
        formStyle={classes.formStyle}
        onFormState={onFormUpdate}
        screens={[
          {
            title: '',
            inputs: buildInputs(),
          },
        ]}
      />
    </>
  );
};

const stringIt = (val) => {
  if (val == undefined || val == null) {
    return undefined;
  }
  return val.toString();
};

export const CreateModal = (props) => {
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState();
  const [formState, setFormState] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const formRef = useRef(null);
  const formValuesRef = useRef({});
  const { me } = useUser();
  const dispatch = useDispatch();
  const { data: taskTypesData } = useTaskTypes();
  const availableTypes = getAvailableTaskTypesForTier(getAvailableTaskTypes(taskTypesData), props.realmTier);

  useEffect(() => {
    if (props.open && props.taskTypeId) {
      formValuesRef.current = { ...formValuesRef.current, taskTypeId: props.taskTypeId };
      setFormState((prev) => ({ ...prev, taskTypeId: props.taskTypeId }));
    }
  }, [props.open]);

  const close = async (e, created = false) => {
    setFormState({});
    formValuesRef.current = {};
    setAlerts();
    props.onClose && (await props.onClose(created));
    setLoading(false);
  };

  const formatData = async () => {
    const relationships = {};
    const fv = { ...formValuesRef.current, ...(formRef.current ? formRef.current.formState : {}) };

    const isSubScore = get(props, 'checkpoint.isSubScore');
    const checklist = props.checklist;
    const checkpoint = props.checkpoint;

    const getName = () => {
      if (get(checkpoint, 'realm.tier') === 'room') {
        return `${get(checkpoint, 'realm.name')} - ${get(checklist, 'name', '')}: ${get(checkpoint, 'name')}`;
      } else {
        return `${get(checklist, 'name', '')}: ${get(checkpoint, 'name')}`;
      }
    };

    const attributes = {
      name: !!props.taskName ? props.taskName : getName(),
      description: get(fv, 'description'),
      status: 0,
      realm_id: stringIt(get(props, 'realmId')),
      assessment_id: stringIt(get(props, 'assessment.id')),
      scorecard_score_id: isSubScore ? undefined : stringIt(get(props, 'checkpoint.scorecardScoreId')),
      sub_score_id: isSubScore ? stringIt(get(props, 'checkpoint.id')) : undefined,
      checkpoint_id: stringIt(get(props, 'checkpoint.checkpointId')),
      client_id: stringIt(get(fv, 'clientId')),
      report_section_id: stringIt(get(props, 'highlight.id')),
      due_date: get(fv, 'dueDate'),
      meta: {
        ...get(fv, 'meta', {}),
      },
      ...(get(fv, 'taskTypeId') ? { task_type_id: fv.taskTypeId } : {}),
    };

    const assignees = get(fv, 'assignedTo');
    set(relationships, 'assignees', {
      data: size(assignees) ? assignees.map((e) => ({ type: 'users', role: 'assignee', id: e.id })) : [{ type: 'users', role: 'assignee', id: me.id }],
    });

    set(relationships, 'authors', {
      data: [{ type: 'users', role: 'author', id: get(me, 'id', '').toString() }],
    });

    const tradeTypes = get(fv, 'tradeTypes');
    if (size(tradeTypes)) {
      set(relationships, 'tradeTypes', {
        data: (tradeTypes || []).map((t, i) => ({ type: 'trade-types', id: t.id, pivot: { order: `${i}` } }))
      })
    }

    const taskData = {
      data: {
        type: 'tasks',
        attributes,
        relationships,
      },
    };

    // Split combined media field into images and documents
    const combinedMedia = get(fv, 'media', []);
    const { images: splitImages, documents: splitDocs } = splitMediaByType(combinedMedia);
    if (size(splitImages)) {
      set(taskData, 'data.attributes.images', splitImages.map((p) => ({ ...p, name: p.path || p.name, caption: get(p, 'caption', '') })));
      await UploadFiles(taskData);
    }
    const documents = splitDocs.map((f) => ({ ...f, name: get(f, 'file.name', f.name) }));
    if (size(documents)) {
      set(taskData, 'data.attributes.files', documents);
      await UploadFiles(taskData, { attributeType: 'files', isImage: false, collection_name: 'task-files' });
    }

    // Process custom field media: patch captions, clean values, add to relationships
    if (get(fv, 'taskTypeId')) {
      await UploadCustomFieldMedia(taskData, {
        rawCfv: get(fv, 'custom_field_values', {}),
        taskTypeId: String(fv.taskTypeId),
      });
    }

    return taskData;
  };

  const submit = async () => {
    if (loading) return;
    if (formRef.current) {
      const result = formRef.current.validateRequired();
      if (!result.passes) return;
    }
    try {
      setLoading(true);
      const data = await formatData();
      const createdTask = await sendData(
        {
          url: `/tasks?include=images,files,assignees,viewers,authors,comments,comments.author,comments.images,comments.files,realm,scorecardScore,scorecardScore.checklist,scorecardScore.checkpoint,scorecardScore.checkpoint.checkpointOptions,tradeTypes`,
          method: 'post',
          data,
        },
        { normalize: { build: ['tasks'] } }
      );
      const createdTypeId = get(formValuesRef.current, 'taskTypeId');
      await close(null, { taskTypeId: createdTypeId });
      const task = get(createdTask, '0');
      dispatch(addNewTask(task));
    } catch (err) {
      setAlerts({
        message: `Failed to create task: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const formUpdate = useCallback((vals) => {
    for (let k in vals) {
      set(formValuesRef.current, k, vals[k]);
    }
    // Only re-render when taskTypeId changes (affects which fields are shown)
    const newTypeId = get(vals, 'taskTypeId');
    if (newTypeId !== undefined) {
      setFormState((prev) => {
        if (get(prev, 'taskTypeId') !== newTypeId) {
          return { ...formValuesRef.current };
        }
        return prev;
      });
    }
    if (formRef.current && formRef.current.canContinue) {
      const result = formRef.current.canContinue(true);
      setIsFormValid((prev) => prev !== result.passes ? result.passes : prev);
    }
  }, []);

  const selectedType = availableTypes.find((tt) => tt.id === get(formState, 'taskTypeId'));
  const modalTitle = selectedType ? `Create ${selectedType.name}` : 'Create Task';

  const pProps = {
    title: modalTitle,
    content: props.open ? <CreateTaskWindow {...props} loading={loading} formState={formState} onFormUpdate={formUpdate} formRef={formRef} /> : null,
    buttons: [
      { title: 'Cancel', onClick: close },
      { title: 'Submit', color: 'primary', variant: 'contained', onClick: submit, style: !isFormValid ? { opacity: 0.5 } : undefined },
    ],
    ...props,
    onClose: close,
  };

  return (
    <>
      <Modal {...pProps} />
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </>
  );
};
